<template>
  <div class="image-uploader">
    <v-file-upload
      v-model="selectedFile"
      data-cy="image-file-input"
      density="compact"
      clearable
      show-size
      :accept="accept"
      :required="required"
      :rules="rules"
      @update:model-value="processFile"
    />
    <v-img
      v-if="preview && previewUrl"
      :src="previewUrl"
      class="mb-4"
      width="100%"
      max-height="320"
      contain
    />
  </div>
</template>

<script>
export default {
  name: 'ImageUploader',
  props: {
    accept: {
      type: String,
      default: 'image/*',
    },
    doNotResize: {
      type: [Array, String],
      default: () => ['gif', 'svg'],
    },
    maxWidth: {
      type: Number,
      default: 2048,
    },
    outputFormat: {
      type: String,
      default: 'blob',
    },
    preview: {
      type: Boolean,
      default: false,
    },
    quality: {
      type: Number,
      default: 0.9,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['input', 'update:modelValue'],
  data() {
    return {
      selectedFile: null,
      previewUrl: '',
    };
  },
  beforeUnmount() {
    this.revokePreview();
  },
  methods: {
    revokePreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
      }
    },
    normalizedFile(value) {
      return Array.isArray(value) ? value[0] : value;
    },
    excludedExtension(file) {
      const configured = Array.isArray(this.doNotResize)
        ? this.doNotResize
        : String(this.doNotResize).replace(/[\[\]'"\s]/g, '').split(',');
      const extension = file.name.split('.').pop().toLowerCase();

      return configured.includes(extension);
    },
    emitResult(result, fileName = null) {
      this.$emit('input', result, fileName);
      this.$emit('update:modelValue', result);
    },
    async processFile(value) {
      const file = this.normalizedFile(value);
      this.revokePreview();

      if (!file) {
        this.emitResult(null);
        return;
      }

      if (this.excludedExtension(file)) {
        if (this.preview) {
          this.previewUrl = URL.createObjectURL(file);
        }
        this.emitResult(file, file.name);
        return;
      }

      const image = await this.loadImage(file);
      const scale = Math.min(1, this.maxWidth / image.naturalWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);

      const mimeType = file.type || 'image/jpeg';
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, this.quality));
      const result = this.outputFormat === 'blob' ? blob : canvas.toDataURL(mimeType, this.quality);

      if (this.preview && blob) {
        this.previewUrl = URL.createObjectURL(blob);
      }
      this.emitResult(result, file.name);
    },
    loadImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        const url = URL.createObjectURL(file);
        image.onload = () => {
          URL.revokeObjectURL(url);
          resolve(image);
        };
        image.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(error);
        };
        image.src = url;
      });
    },
  },
};
</script>
